import { Danger } from 'iconsax-reactjs';
import { useI18n } from '@/lib/i18n';
import { formatFeedClock, useFeedHealth } from '@/lib/useFeedHealth';
import { formatAge } from '@/components/FeedStatus';

/**
 * Full-width red bar under the top bar whenever MT5 data is not live.
 *
 * Loud on purpose. A stopped feed leaves every chart and verdict frozen on old
 * prices while looking perfectly normal, and price alerts silently stop firing
 * — the one failure here that can cost money without anything looking wrong.
 * It disappears by itself as soon as prices flow again.
 */
export function FeedBanner() {
  const { t } = useI18n();
  const { state, updatedAt, age } = useFeedHealth();
  if (state === 'live' || state === 'loading') return null;

  const reason =
    state === 'disconnected'
      ? t('feed.bannerDisconnected')
      : state === 'unreachable'
        ? t('feed.bannerUnreachable')
        : t('feed.bannerStale');

  return (
    <div role="alert" className="bg-danger text-danger-foreground shadow-md">
      <div className="mx-auto flex w-full max-w-[1600px] flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 sm:px-6">
        <Danger size={22} variant="Bold" className="shrink-0" />
        <p className="text-base font-semibold">{t('feed.bannerTitle')}</p>
        <p className="text-sm opacity-95">
          {reason}
          {updatedAt !== null && (
            <>
              {' · '}
              {t('feed.bannerLast', { time: formatFeedClock(updatedAt), age: formatAge(age!, t) })}
            </>
          )}
        </p>
      </div>
    </div>
  );
}
